<script setup lang="ts">
/**
 * The Auth Nav
 * --------------------------------
 *
 */

const localeRoute = useLocaleRoute();
const { isAuthenticated, login, logout } = useAuth();
const { user } = useLoadUser();

const isProfilePage = computed(() => (useRoute().name as string ?? '').includes('esav-profile'));

/**
 * Route of last page in offer flow, to go back to it from profile page
 */
const lastFlowRoute = computed(() => {
    if (!isProfilePage.value) {
        return undefined;
    }
    try {
        return JSON.parse(sessionStorage.getItem('lastFlowRoute') ?? '{}');
    }
    catch (error) {
        return undefined;
    }
});

/**
 * Login or go to profile
 */
function clickProfile() {
    if (isAuthenticated.value) {
        navigateTo(localeRoute({ name: 'esav-profile' }));
    }
    else {
        login();
    }
}

/**
 * log out
 */
function doLogout() {
    logout(localeRoute({ name: 'loggedout' }));
}

/**
 * Go back to offer flow
 */
function goBack() {
    navigateTo(lastFlowRoute.value);
}
</script>

<template>
    <div class="print:hidden" data-sentry-mask>
        <div v-if="isProfilePage" class="flex items-center divide-x divide-shade-2">
            <button v-if="!!lastFlowRoute" class="flex items-center space-x-2 pr-3" @click="goBack">
                <Icon name="tot:backward-small" class="size-3" />
                <span class="typo-meta-s">{{ $t('core.auth.backtoOfferFlow') }}</span>
            </button>

            <button class="flex items-center space-x-2 pl-1 sm:pl-3" @click="() => doLogout()">
                <span class="typo-meta-s max-sm:hidden">{{ $t('core.auth.logout') }}</span>
                <Icon name="tot:logout" class="size-4" />
            </button>
        </div>
        <button
            v-else
            class="cursor-pointer"
            @click="clickProfile"
        >
            <span
                class="flex size-7 items-center justify-center rounded-full  typo-meta-s-bold text-white"
                :class="isAuthenticated ? 'bg-brand-b' : 'bg-shade-3'"
            >
                <span v-if="isAuthenticated">
                    {{ user?.details.firstName.charAt(0).toUpperCase() }}{{ user?.details.lastName.charAt(0).toUpperCase() }}
                </span>
                <template v-else>
                    <span class="sr-only">
                        {{ $t('core.auth.login') }}
                    </span>
                    <Icon name="tot:user" class="size-4" />
                </template>
            </span>
        </button>
    </div>
</template>
